import { useEffect, useRef, useState } from "react";
import { RiBookmarkLine, RiBookmark2Line, RiCloseLine } from "react-icons/ri";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Button from "../../Utility/Button/Button";
import Input from "../../Utility/Input/Input";

const ALL_VALUE = "all";

/**
 * Saved-filter selector (left) + optional Save As (right) using shared Button / Input.
 */
const SavedSearchBar = ({
  savedSearches = [],
  activeSavedSearchId = null,
  onSelect,
  onDelete,
  isDirty = false,
  onSaveAs,
  saving = false,
}) => {
  const [saveAsMode, setSaveAsMode] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");
  const saveAsInputRef = useRef(null);

  useEffect(() => {
    if (saveAsMode && saveAsInputRef.current) {
      saveAsInputRef.current.focus();
    }
  }, [saveAsMode]);

  const handleSaveAsSubmit = async (e) => {
    e.preventDefault();
    if (!saveAsName.trim()) return;
    await onSaveAs?.(saveAsName.trim());
    setSaveAsName("");
    setSaveAsMode(false);
  };

  if (savedSearches.length === 0 && !isDirty) return null;

  const hasDropdown = savedSearches.length > 0;
  const showSaveAs = isDirty && typeof onSaveAs === "function";

  const options = hasDropdown
    ? [
        { label: "All Work Items", value: ALL_VALUE },
        ...savedSearches.map((s) => ({ label: s.name, value: String(s.id) })),
      ]
    : [];

  const value =
    activeSavedSearchId != null && activeSavedSearchId !== ""
      ? String(activeSavedSearchId)
      : ALL_VALUE;

  const handleChange = (e) => {
    const id = e.target.value;
    if (!id || id === ALL_VALUE) {
      onSelect?.({ id: null, filters: null });
    } else {
      const search = savedSearches.find((s) => String(s.id) === String(id));
      if (search) onSelect?.(search);
    }
  };

  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-y-2 gap-x-3 px-1 py-2 mb-1">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {hasDropdown && (
          <>
            <RiBookmarkLine size={14} className="shrink-0 text-gray-400" />
            <span className="shrink-0 text-xs text-gray-500">Saved filter:</span>
            <Dropdown
              name="savedFilter"
              value={value}
              onChange={handleChange}
              options={options}
              placeholder="All Work Items"
              noWrapper={true}
              clearable={false}
              preserveOptionOrder={true}
              className="min-w-[160px] text-xs"
            />
            {activeSavedSearchId && (
              <Button
                type="button"
                variant="icon"
                size="vsm"
                onClick={() => onDelete?.(activeSavedSearchId)}
                className="flex h-5 min-w-[20px] items-center justify-center rounded p-0 text-gray-400 transition-colors hover:text-red-500"
                aria-label="Delete saved search"
              >
                <RiCloseLine size={14} />
              </Button>
            )}
          </>
        )}
      </div>

      {showSaveAs ? (
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {saveAsMode ? (
            <form onSubmit={handleSaveAsSubmit} className="inline-flex items-center gap-1.5">
              <Input
                ref={saveAsInputRef}
                name="saveAsName"
                type="text"
                value={saveAsName}
                onChange={(e) => setSaveAsName(e.target.value)}
                placeholder="Enter search name…"
                maxLength={100}
                noWrapper={true}
                className="!h-[30px] !min-h-0 !w-44 !rounded-lg !px-2.5 !py-0 !text-xs"
              />
              <Button
                type="submit"
                disabled={saving || !saveAsName.trim()}
                variant="primary"
                size="vsm"
                className="!h-[30px] !min-h-[30px] !rounded-lg !px-2.5 !py-0 !text-xs"
              >
                {saving ? "…" : "Save"}
              </Button>
              <Button
                type="button"
                variant="neutral"
                size="vsm"
                onClick={() => {
                  setSaveAsMode(false);
                  setSaveAsName("");
                }}
                className="!h-[30px] !min-h-[30px] !rounded-lg !px-2.5 !py-0 !text-xs"
              >
                Cancel
              </Button>
            </form>
          ) : (
            <Button
              type="button"
              variant="neutral"
              size="vsm"
              onClick={() => setSaveAsMode(true)}
              className="!inline-flex !h-[30px] !min-h-[30px] !items-center !gap-1.5 !rounded-lg !border !border-border-primary !bg-surface-primary !px-3 !py-0 !text-xs !font-medium !normal-case hover:!bg-[var(--bg-secondary)]"
            >
              <RiBookmark2Line size={12} className="shrink-0" />
              Save As…
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SavedSearchBar;
